import { StoreChangeRequestApprovalPanel } from "@ubif/components";
import {
  type StoreAddressContactChangeApprovalData,
  type StoreChangeRequestApprovalActionDetail,
  type StoreChangeRequestResolutionSummary,
  type StoreChangeRequestType,
  StoreChangeRequestUpdateStatus,
  type StoreHoursChangeApprovalData
} from "@ubif/types";
import {
  extractErrorMessage,
  selectUser,
  showToast,
  useGetStoreChangeRequestQuery,
  useGlobalDispatch,
  useUpdateStoreChangeRequestMutation
} from "@ubif/util-store";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  type MappedStoreChangeRequest,
  getStoreChangeReasonDropdownOptions,
  isFetchBaseQueryError,
  mapStoreChangeRequest
} from "./storeChangeRequestMappers";

const QUERY_PARAM = "storeChangeRequest";

export function StoreChangeRequestPanelContainer({ requestIdParam }: { requestIdParam?: string }) {
  const { t } = useTranslation("root-config");
  const globalDispatch = useGlobalDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const loggedInUser = useSelector(selectUser);
  const reasonDropdownOptions = getStoreChangeReasonDropdownOptions(t);

  const requestId = requestIdParam ?? searchParams.get(QUERY_PARAM);

  // The approver "new request" notification opens the panel at the domain root (`/?storeChangeRequest=`),
  // whereas requestor-facing entry points (e.g. MyStore banners / outcome notifications) live under a
  // sub-path like `/admin/my-store`. When the logged-in user is both the requestor and an approver, the
  // root entry point should let them approve/deny; any other path keeps them in the requestor (cancel) role.
  const openedFromDomainRoot = location.pathname === "/";

  const { data, isSuccess, isLoading, isFetching, error, isError } = useGetStoreChangeRequestQuery(requestId ?? "", {
    skip: !requestId
  });
  const [updateStoreChangeRequest] = useUpdateStoreChangeRequestMutation();

  // While the request id arg changes, RTK Query can keep the previous id's data with isSuccess still true.
  // Only map/display once the in-flight GET for the current requestId has finished.
  const panelLoading = Boolean(requestId) && !isError && (isLoading || isFetching);
  const requestData = panelLoading ? undefined : data;

  const mapped = useMemo((): MappedStoreChangeRequest | null => {
    if (requestData == null) return null;
    return mapStoreChangeRequest(requestData, reasonDropdownOptions);
  }, [requestData]);

  const requestAuditIds = requestData?.impactedStores?.length
    ? requestData.impactedStores.map((store) => store.requestAuditId).filter((id) => Number.isFinite(id))
    : [];

  const clearParam = useCallback(() => {
    const next = new URLSearchParams(location.search);
    next.delete(QUERY_PARAM);
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash },
      { replace: true }
    );
  }, [navigate, location.pathname, location.search, location.hash]);

  const lastErroredId = useRef<string | null>(null);
  useEffect(() => {
    if (!requestId || error == null || lastErroredId.current === requestId) return;
    lastErroredId.current = requestId;

    const errorStatus = isFetchBaseQueryError(error) ? error.status : undefined;
    if (errorStatus === 400) {
      globalDispatch(showToast({ message: t("StoreChangeRequest.Errors.NotAuthorized"), toastType: "error" }));
      clearParam();
    } else if (errorStatus === 404) {
      globalDispatch(showToast({ message: t("StoreChangeRequest.Errors.NotFound"), toastType: "error" }));
      clearParam();
    } else {
      globalDispatch(showToast({ message: t("StoreChangeRequest.Errors.Generic"), toastType: "error" }));
    }
  }, [requestId, error, globalDispatch, t, clearParam]);

  useEffect(() => {
    if (lastErroredId.current && lastErroredId.current !== requestId) {
      lastErroredId.current = null;
    }
  }, [requestId]);
  const panelOpen = Boolean(requestId) && (panelLoading || (isSuccess && requestData != null));
  const panelRequestType: StoreChangeRequestType | null = mapped?.requestType ?? null;
  const panelAddressData: StoreAddressContactChangeApprovalData = mapped?.addressData ?? EMPTY_ADDRESS_DATA;
  const panelBusinessHoursData: StoreHoursChangeApprovalData =
    mapped?.businessHoursData ?? buildEmptyHoursData("businessHours");
  const panelTemporaryClosureData: StoreHoursChangeApprovalData =
    mapped?.temporaryClosureData ?? buildEmptyHoursData("temporaryClosure");
  const panelResolutionSummary: StoreChangeRequestResolutionSummary | null = mapped?.resolutionSummary ?? null;

  const handleApprove = async () => {
    if (!requestAuditIds.length) {
      globalDispatch(showToast({ message: t("StoreChangeRequest.Errors.Generic"), toastType: "error" }));
      return null;
    }

    try {
      await updateStoreChangeRequest({
        requestIds: requestAuditIds,
        status: StoreChangeRequestUpdateStatus.Approved
      }).unwrap();
      globalDispatch(
        showToast({
          message: t("StoreChangeRequest.Success.Approved", {
            requestorName: requestData?.actions?.find((a) => a.action === "requested")?.user ?? ""
          }),
          toastType: "success"
        })
      );
      clearParam();
    } catch (error: unknown) {
      const message = extractErrorMessage(error, t("StoreChangeRequest.Errors.Generic"));
      if (message) {
        globalDispatch(showToast({ message, toastType: "error" }));
      }
    }
  };

  const handleDeny = async (detail: StoreChangeRequestApprovalActionDetail) => {
    if (!requestAuditIds.length) {
      globalDispatch(showToast({ message: t("StoreChangeRequest.Errors.Generic"), toastType: "error" }));
      return null;
    }

    try {
      await updateStoreChangeRequest({
        requestIds: requestAuditIds,
        status: StoreChangeRequestUpdateStatus.Declined,
        noteText: detail.denyReason?.trim() || undefined
      }).unwrap();
      globalDispatch(
        showToast({
          message: t("StoreChangeRequest.Success.Denied", {
            requestorName: requestData?.actions?.find((a) => a.action === "requested")?.user ?? ""
          }),
          toastType: "success"
        })
      );
      clearParam();
    } catch (error: unknown) {
      const message = extractErrorMessage(error, t("StoreChangeRequest.Errors.Generic"));
      if (message) {
        globalDispatch(showToast({ message, toastType: "error" }));
      }
    }
  };

  const handleCancelRequest = useCallback(async () => {
    if (data == null || requestId == null || requestId === "") {
      return null;
    }

    const requestIds = Array.from(
      new Set(
        data.impactedStores
          .map((s) => s.requestAuditId)
          .filter((id): id is number => typeof id === "number" && Number.isFinite(id) && id > 0)
      )
    );

    if (requestIds.length === 0) {
      return null;
    }

    try {
      await updateStoreChangeRequest({
        requestIds,
        status: StoreChangeRequestUpdateStatus.Cancelled
      }).unwrap();
      globalDispatch(showToast({ message: t("StoreChangeRequest.CancelSuccess"), toastType: "success", time: 5000 }));
      clearParam();
    } catch (error: unknown) {
      const message = extractErrorMessage(error, t("StoreChangeRequest.Errors.Generic"));
      globalDispatch(showToast({ message, toastType: "error" }));
      throw error;
    }
  }, [clearParam, data, globalDispatch, requestId, t, updateStoreChangeRequest]);

  return (
    <StoreChangeRequestApprovalPanel
      open={panelOpen}
      isLoading={panelLoading}
      requestType={panelRequestType}
      requestId={requestId ?? undefined}
      auditStatus={requestData?.status}
      resolutionSummary={panelResolutionSummary}
      onClose={clearParam}
      onApprove={handleApprove}
      onDeny={handleDeny}
      onCancelRequest={handleCancelRequest}
      addressRequestData={panelAddressData}
      businessHoursRequestData={panelBusinessHoursData}
      temporaryClosureRequestData={panelTemporaryClosureData}
      loggedInUser={loggedInUser}
      actions={requestData?.actions}
      openedFromDomainRoot={openedFromDomainRoot}
    />
  );
}

const EMPTY_ADDRESS_DATA: StoreAddressContactChangeApprovalData = {
  requestorName: "",
  requestorUserId: 0,
  requestedDate: "",
  impactedStore: "",
  effectiveDate: "",
  showPhoneNumberSection: false,
  showAddressSection: false
};

function buildEmptyHoursData(requestType: "businessHours" | "temporaryClosure"): StoreHoursChangeApprovalData {
  return {
    requestType,
    requestorName: "",
    requestorUserId: 0,
    requestedDate: "",
    impactedStores: [],
    dayRows: [],
    effectiveDate: "",
    reasonForChange: "",
    status: undefined
  };
}
